import { NavLink, Outlet, useNavigate } from "react-router";
import { AppButton } from "@/components/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "../auth/auth-store";
import { handleSignOut } from "../auth/auth-service";
import { useCurrentOrganization } from "../hooks/use-current-organization";
import { Home, MessageSquare } from "lucide-react";

type ThemePreference = "light" | "dark";
const THEME_STORAGE_KEY = "theme-preference";
const DEFAULT_THEME: ThemePreference = "light";

function getStoredTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }
  } catch {
    // Ignore storage errors
  }
  return DEFAULT_THEME;
}

function applyTheme(theme: ThemePreference): void {
  const html = document.documentElement;
  html.classList.remove("light", "dark");
  html.classList.add(theme);
  html.setAttribute("data-theme", theme);
}

export function AppShell(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const { role } = useCurrentOrganization();
  const [theme, setTheme] = useState<ThemePreference>(getStoredTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next: ThemePreference = prev === "light" ? "dark" : "light";
      localStorage.setItem(THEME_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const onSignOut = useCallback(async () => {
    await handleSignOut();
  }, []);

  const onNavigateHome = useCallback(() => {
    void navigate("/home");
  }, [navigate]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <nav className="flex items-center justify-between border-b border-border px-6 py-3">
        <div className="flex items-center">
          <AppButton variant="ghost" onPress={onNavigateHome} className="text-lg font-bold">
            {t("nav.appName")}
          </AppButton>
        </div>

        <div className="flex items-center">
          <AppButton variant="ghost" onPress={onNavigateHome}>
            {t("nav.home")}
          </AppButton>
        </div>

        <div className="flex items-center gap-2">
          <AppButton
            isIconOnly
            variant="ghost"
            aria-label={t("nav.toggleTheme")}
            onPress={toggleTheme}
            size="sm"
          >
            {theme === "light" ? "🌙" : "☀️"}
          </AppButton>
          {isAuthenticated && user && (
            <>
              <span className="text-sm text-foreground-muted">{user.email}</span>
              <AppButton variant="danger" size="sm" onPress={onSignOut}>
                {t("common.signOut")}
              </AppButton>
            </>
          )}
        </div>
      </nav>

      <div className="flex flex-1">
        <aside className="hidden w-56 shrink-0 border-r border-border bg-surface p-4 md:block">
          <nav aria-label={t("nav.sidebar")}>
            <ul className="space-y-1">
              <li>
                <NavLink
                  to="/home"
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "active bg-accent/10 font-medium text-accent"
                        : "text-foreground-secondary hover:bg-surface-hover"
                    }`
                  }
                >
                  <Home size={16} />
                  {t("nav.home")}
                </NavLink>
              </li>
            </ul>

            {(role === "OWNER" || role === "ADMIN") && (
              <div className="mt-6">
                <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  {t("nav.settings")}
                </p>
                <ul className="space-y-1">
                  <li>
                    <NavLink
                      to="/settings/slack-bot"
                      className={({ isActive }) =>
                        `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                          isActive
                            ? "active bg-accent/10 font-medium text-accent"
                            : "text-foreground-secondary hover:bg-surface-hover"
                        }`
                      }
                    >
                      <MessageSquare size={16} />
                      {t("nav.slackBot")}
                    </NavLink>
                  </li>
                </ul>
              </div>
            )}
          </nav>
        </aside>

        <main className="flex-1 p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
